import { extractDomainFromEmail } from 'features/SSO/utils'
import type { KingCountyPilotPayload } from 'redux/KingCounty/kingCountyTypes'

/** King County e-bike pilot — shared helpers for KC-specific UI and validation. */

export const KC_ALLOWED_EMAIL_DOMAINS = [
  'kingcounty.gov',
  'soundtransit.org',
] as const

export const KC_WORKSITE_LOCATIONS = [
  'East Base Facilities',
  'Work Center Facilities',
  'Default Location',
  'Vashon Pit',
  'Prob. Brch Federal Way Ofc.',
  'North Public Health Center',
  'North Shore Public Health Ctr',
  'White Ctr Public Hlth Ctr',
  'Safety & Training Facility',
  'Renton (South) Treatment Plant',
  'Youth/Teen Prgm',
  'South Base',
  'Children & Family Justice Ctr',
  'Elections,Archives,Records W/H',
  'Ryerson Base',
  'Component Supply Center',
  'Atlantic/Central Base',
  'Non-Revenue Vehicle Maint',
  'Cedar River Maintenance Shop',
  'Construction Mgmt.',
  'Sunset Maintenance Shop',
  'Kent/Alder Square',
  'Eastgate Health Center',
  'Prct 4 - Burien PD',
  'Distribution Warehouse',
  'Renton Maintenance Facility',
  'Atlantic Base',
  'North Facilities Maintenance',
  'Cedar Hills',
  'Shoreline District Court',
  'Prct 2 - Kenmore PD',
  'Cougar/ Squak Maintenance Shop',
  'Cottage Lake Park',
  'East Base',
  'Auburn Health Center',
  'King Street Center',
  'Downtown Health Center',
  'Kent Animal Control Control',
  'South Recreation District',
  'Power Distribution Section',
  'Soos Creek Maintenance Shop',
  'Renton Public Health Ctr',
  'Renton Shop',
  'Environmental Lab',
  'Harborview',
  'Federal Way Public Hlth Ctr',
  'Five Mile Park',
  'Tolt McDonald Park',
  'Yesler Building',
  'Chinook Building',
  'Fall City Pit',
  'Columbia Health Center',
  'KC Correctional Facility',
  'Administration Building',
  'KC Court House',
  'Lucille Street Warehouse',
  'Bellevue District Court',
  'Burien District Court',
  'Marymoor Park',
  'Tuberculosis (TB) Control',
  'Kent Teen Clinic',
  'Jail Health - Kent',
  'Weyerhaeuser Aquatic Center',
  'Sabey Data Center',
  '7277 Building (Airport Admin)',
  'E-911 & Safety~Claims Ofcs',
  'Skykomish Pit',
  'Central Building',
  'Exchange Building',
  'Redmond District Court',
  'Black River',
  'North Utility Crew',
  'Wells Fargo Center',
  'Medic One',
  'Bellevue Base',
  'North Base',
  'Brightwater Project Office',
  'Jameson Street Building',
  'South Lake Union Streetcar',
  'Op & Maint Facility/Light Rail',
  'Graybar Building',
  'Radio Communication Services',
  'Prct 5 - Shoreline PD',
  'Downtown Needle Exchange',
  'Criminal Justice Training Ctr',
  'East Satellite Construction',
  'Cadman Pit',
  'Diamond Pit',
  'Boeing Field',
  'Orcas Building',
  'RCECC Regl Com Emer Coord Cntr',
  'West Point Treatment Plant',
  'Maleng Regional Justice Center',
  'Prct 6 - Sea Tac PD',
  'Work Source Renton',
  'Issaquah District Court',
  'KC Elections',
  'Prct 3 - Covington PD',
  'KC Metro Transit PD',
  'International Airport PD/ARFF',
  'Prct 3 - Maple Valley PD',
  'Prct 3 - Muckleshoot Tribal PD',
  'Prct 3 - Newcastle PD',
  'Prct 2 - Sammamish PD',
  'Prct 2 - Woodinville PD',
  'PMU - Property Management Unit',
  'Shoreline Community College',
  'Radovich Properties',
  'Jail Health - Seattle',
  'Multi Locns Neo-Gov',
  'Regional Justice Center - Kent',
  'Prct 2 - Headquarters',
  'Prct 3 - Headquarters',
  'Prct 4 - Headquarters',
  'Kent Public Health Center',
  'Sound Transit',
  'Central Base Ops',
  'Transit Control Center',
  'South Construction',
  'Prob. Brch Bellevue Ofc',
  'Duthie Hill Park',
  'Auburn District Court',
  'Kendall Lake',
  'Jefferson Building',
  'Meeker Street Building',
  'First Hill Streetcar',
  'Walter Scott Brown Building',
  'Dexter Horton Building',
  'WorkSourceYouthSourceRentonVet',
  'Central Facilities Maintenance',
  'South Facilities Maintenance',
  'Kent Valley Professional Cntr',
  'ShoWare Center',
  'Northgate Executive Center II',
  'Second and Seneca Building',
  'Other / not listed',
] as const

/** Matches `KingCountyEmployee.CyclingExperience` (backend). */
export type KcCyclingExperience = 'LOW' | 'MEDIUM' | 'HIGH'

/** Matches `KingCountyEmployee.Terrain` codes (backend). */
export type KcTerrainCode =
  | 'STEEP_HILLS'
  | 'WELL_MAINTAINED'
  | 'ROUGH'
  | 'DIRT_GRAVEL'

export const KC_COMMUTE_MODE_OTHER = 'Other'

export type KcQuestionnaireDropdownItem = { id: string; text: string }

/** Form shape aligned with `KingCountyEmployee` pilot fields (camelCase for TS). */
export type KingCountyQuestionnaireValues = {
  commuteMode: string
  commuteModeSelection: string
  worksite: string
  cyclingExperience: KcCyclingExperience | ''
  transitCombination: boolean | null
  cargoOrKidsTransport: boolean | null
  householdOwnsEbike: boolean | null
  terrain: KcTerrainCode[]
  commuteFrequencyDaysPerWeek: number | null
}

/** Which questionnaire fields failed validation (for `DropdownV2` `error`, etc.). */
export type KingCountyQuestionnaireInvalid = {
  commuteMode: boolean
  worksite: boolean
  cyclingExperience: boolean
  transitCombination: boolean
  cargoOrKidsTransport: boolean
  householdOwnsEbike: boolean
  terrain: boolean
  commuteFrequencyDaysPerWeek: boolean
}

export const emptyKingCountyQuestionnaireInvalid =
  (): KingCountyQuestionnaireInvalid => ({
    commuteMode: false,
    worksite: false,
    cyclingExperience: false,
    transitCombination: false,
    cargoOrKidsTransport: false,
    householdOwnsEbike: false,
    terrain: false,
    commuteFrequencyDaysPerWeek: false,
})

export const KC_CYCLING_EXPERIENCE_ITEMS: {
  id: KcCyclingExperience
  text: string
}[] = [
  { id: 'LOW', text: 'Low' },
  { id: 'MEDIUM', text: 'Medium' },
  { id: 'HIGH', text: 'High' },
]

export const KC_TERRAIN_OPTIONS: { id: KcTerrainCode; label: string }[] = [
  { id: 'STEEP_HILLS', label: 'Steep hills' },
  { id: 'WELL_MAINTAINED', label: 'Well-maintained' },
  { id: 'ROUGH', label: 'Rough' },
  { id: 'DIRT_GRAVEL', label: 'Dirt / gravel' },
]

/** 0–7 to match `KingCountyEmployee.commute_frequency_days_per_week` validators. */
export const KC_COMMUTE_FREQUENCY_ITEMS = [0, 1, 2, 3, 4, 5, 6, 7].map((n) => ({
  id: String(n),
  text: n === 1 ? '1 day / week' : `${n} days / week`,
}))

export const KC_COMMUTE_MODE_ITEMS: KcQuestionnaireDropdownItem[] = [
  { id: 'Drive alone', text: 'Drive alone' },
  {
    id: 'Bus / light rail / commuter train',
    text: 'Bus / light rail / commuter train',
  },
  { id: 'Bike / scooter', text: 'Bike / scooter' },
  { id: 'Carpool', text: 'Carpool' },
  { id: 'Vanpool', text: 'Vanpool' },
  { id: 'Ferry / water taxi', text: 'Ferry / water taxi' },
  { id: 'Walk', text: 'Walk' },
  { id: 'Multimodal', text: 'Multimodal' },
  { id: KC_COMMUTE_MODE_OTHER, text: KC_COMMUTE_MODE_OTHER },
]

/** `DropdownV2` sentinel id for “no selection yet”. */
export const KC_QUESTIONNAIRE_UNSET_ID = 'UNSET'

const KC_TRANSIT_DROPDOWN_YES = 'yes'
const KC_TRANSIT_DROPDOWN_NO = 'no'

const KC_BOOLEAN_DROPDOWN_TRUE = 'true'
const KC_BOOLEAN_DROPDOWN_FALSE = 'false'

export const KC_TRUE_FALSE_DROPDOWN_ITEMS: KcQuestionnaireDropdownItem[] = [
  { id: KC_QUESTIONNAIRE_UNSET_ID, text: 'Select one…' },
  { id: KC_BOOLEAN_DROPDOWN_TRUE, text: 'Yes' },
  { id: KC_BOOLEAN_DROPDOWN_FALSE, text: 'No' },
]

export const KC_CYCLING_EXPERIENCE_DROPDOWN_ITEMS: KcQuestionnaireDropdownItem[] =
  [
    { id: KC_QUESTIONNAIRE_UNSET_ID, text: 'Select one…' },
    ...KC_CYCLING_EXPERIENCE_ITEMS,
  ]

export const KC_TRANSIT_COMBINATION_DROPDOWN_ITEMS: KcQuestionnaireDropdownItem[] =
  [
    { id: KC_QUESTIONNAIRE_UNSET_ID, text: 'Select one…' },
    { id: KC_TRANSIT_DROPDOWN_YES, text: 'Yes' },
    { id: KC_TRANSIT_DROPDOWN_NO, text: 'No' },
  ]

export const KC_COMMUTE_FREQUENCY_DROPDOWN_ITEMS: KcQuestionnaireDropdownItem[] =
  [
    { id: KC_QUESTIONNAIRE_UNSET_ID, text: 'Select one…' },
    ...KC_COMMUTE_FREQUENCY_ITEMS,
  ]

export const KC_COMMUTE_MODE_DROPDOWN_ITEMS: KcQuestionnaireDropdownItem[] = [
  { id: KC_QUESTIONNAIRE_UNSET_ID, text: 'Select one…' },
  ...KC_COMMUTE_MODE_ITEMS,
]

export const KC_QUESTIONNAIRE_TITLE = 'Please Answer The Following Questions'
export const KC_QUESTIONNAIRE_SUBTITLE =
  'Required for the King County pilot; used for program matching.'
export const KC_QUESTIONNAIRE_LABEL_COMMUTE_MODE = 'Commute Mode *'
export const KC_QUESTIONNAIRE_LABEL_WORKSITE = 'Worksite *'
export const KC_QUESTIONNAIRE_LABEL_COMMUTE_FREQUENCY =
  'Commute frequency (days per week) *'
export const KC_QUESTIONNAIRE_LABEL_CYCLING = 'Cycling experience *'
export const KC_QUESTIONNAIRE_LABEL_TRANSIT =
  'Do you combine your commute with transit? *'
export const KC_QUESTIONNAIRE_LABEL_CARGO =
  'I need to carry cargo or transport children on my commute *'
export const KC_QUESTIONNAIRE_LABEL_HOUSEHOLD_EBIKE =
  'My household currently owns an e-bike *'
export const KC_QUESTIONNAIRE_LABEL_TERRAIN_HEADER =
  'Terrain on your route (select all that apply) *'
export const KC_QUESTIONNAIRE_TERRAIN_ERROR =
  'Select at least one terrain option.'

export function kingCountyQuestionnaireCyclingActiveId(
  v: KingCountyQuestionnaireValues
): string {
  return v.cyclingExperience || KC_QUESTIONNAIRE_UNSET_ID
}

export function kingCountyQuestionnaireTransitActiveId(
  v: KingCountyQuestionnaireValues
): string {
  if (v.transitCombination === null) return KC_QUESTIONNAIRE_UNSET_ID
  return v.transitCombination ? KC_TRANSIT_DROPDOWN_YES : KC_TRANSIT_DROPDOWN_NO
}

export function kingCountyQuestionnaireCommuteFrequencyActiveId(
  v: KingCountyQuestionnaireValues
): string {
  if (v.commuteFrequencyDaysPerWeek !== null) {
    return String(v.commuteFrequencyDaysPerWeek)
  }
  return KC_QUESTIONNAIRE_UNSET_ID
}

export function kingCountyQuestionnaireCommuteModeActiveId(
  v: KingCountyQuestionnaireValues
): string {
  return v.commuteModeSelection || KC_QUESTIONNAIRE_UNSET_ID
}

function kingCountyBooleanActiveId(value: boolean | null): string {
  if (value === null) return KC_QUESTIONNAIRE_UNSET_ID
  return value ? KC_BOOLEAN_DROPDOWN_TRUE : KC_BOOLEAN_DROPDOWN_FALSE
}

export function kingCountyQuestionnaireCargoActiveId(
  v: KingCountyQuestionnaireValues
): string {
  return kingCountyBooleanActiveId(v.cargoOrKidsTransport)
}

export function kingCountyQuestionnaireHouseholdEbikeActiveId(
  v: KingCountyQuestionnaireValues
): string {
  return kingCountyBooleanActiveId(v.householdOwnsEbike)
}

export function withKingCountyCyclingFromDropdownId(
  v: KingCountyQuestionnaireValues,
  id: string
): KingCountyQuestionnaireValues {
  if (id === KC_QUESTIONNAIRE_UNSET_ID) {
    return { ...v, cyclingExperience: '' }
  }
  return { ...v, cyclingExperience: id as KcCyclingExperience }
}

export function withKingCountyTransitFromDropdownId(
  v: KingCountyQuestionnaireValues,
  id: string
): KingCountyQuestionnaireValues {
  if (id === KC_QUESTIONNAIRE_UNSET_ID) {
    return { ...v, transitCombination: null }
  }
  return { ...v, transitCombination: id === KC_TRANSIT_DROPDOWN_YES }
}

export function withKingCountyCommuteFrequencyFromDropdownId(
  v: KingCountyQuestionnaireValues,
  id: string
): KingCountyQuestionnaireValues {
  if (id === KC_QUESTIONNAIRE_UNSET_ID) {
    return { ...v, commuteFrequencyDaysPerWeek: null }
  }
  return { ...v, commuteFrequencyDaysPerWeek: parseInt(id, 10) }
}

export function withKingCountyCommuteModeFromDropdownId(
  v: KingCountyQuestionnaireValues,
  id: string
): KingCountyQuestionnaireValues {
  if (id === KC_QUESTIONNAIRE_UNSET_ID) {
    return { ...v, commuteModeSelection: '', commuteMode: '' }
  }
  if (id === KC_COMMUTE_MODE_OTHER) {
    return { ...v, commuteModeSelection: id, commuteMode: '' }
  }
  return { ...v, commuteModeSelection: id, commuteMode: id }
}

export function withKingCountyCommuteModeOtherText(
  v: KingCountyQuestionnaireValues,
  commuteMode: string
): KingCountyQuestionnaireValues {
  return { ...v, commuteMode }
}

export function withKingCountyWorksite(
  v: KingCountyQuestionnaireValues,
  worksite: string
): KingCountyQuestionnaireValues {
  return { ...v, worksite }
}

export function sortKingCountyWorksiteLocations(
  locations: readonly string[],
  searchValue: string
): string[] {
  const normalizedSearch = searchValue.trim().toLowerCase()
  if (!normalizedSearch) return [...locations]

  return [...locations]
    .filter((location) => location.toLowerCase().includes(normalizedSearch))
    .sort((a, b) => {
      const aLower = a.toLowerCase()
      const bLower = b.toLowerCase()
      const aIndex = aLower.indexOf(normalizedSearch)
      const bIndex = bLower.indexOf(normalizedSearch)

      if (aIndex !== bIndex) return aIndex - bIndex
      return aLower.localeCompare(bLower)
    })
}

export function withKingCountyTerrainToggled(
  v: KingCountyQuestionnaireValues,
  code: KcTerrainCode
): KingCountyQuestionnaireValues {
  const has = v.terrain.includes(code)
  const terrain = has
    ? v.terrain.filter((t) => t !== code)
    : [...v.terrain, code]
  return { ...v, terrain }
}

function booleanFromDropdownId(id: string): boolean | null {
  if (id === KC_QUESTIONNAIRE_UNSET_ID) return null
  return id === KC_BOOLEAN_DROPDOWN_TRUE
}

export function withKingCountyCargoFromDropdownId(
  v: KingCountyQuestionnaireValues,
  id: string
): KingCountyQuestionnaireValues {
  return { ...v, cargoOrKidsTransport: booleanFromDropdownId(id) }
}

export function withKingCountyHouseholdOwnsEbikeFromDropdownId(
  v: KingCountyQuestionnaireValues,
  id: string
): KingCountyQuestionnaireValues {
  return { ...v, householdOwnsEbike: booleanFromDropdownId(id) }
}

export function isKingCountyTerrainSelected(
  v: KingCountyQuestionnaireValues,
  code: KcTerrainCode
): boolean {
  return v.terrain.includes(code)
}

export function toKingCountyPilotPayload(
  v: KingCountyQuestionnaireValues
): KingCountyPilotPayload {
  return {
    commuteMode: v.commuteMode.trim(),
    worksite: v.worksite.trim(),
    cyclingExperience: v.cyclingExperience as KcCyclingExperience,
    transitCombination: v.transitCombination as boolean,
    cargoOrKidsTransport: v.cargoOrKidsTransport as boolean,
    householdOwnsEbike: v.householdOwnsEbike as boolean,
    terrain: v.terrain,
    commuteFrequencyDaysPerWeek: v.commuteFrequencyDaysPerWeek as number,
  }
}

export const emptyKingCountyQuestionnaireValues =
  (): KingCountyQuestionnaireValues => ({
    commuteMode: '',
    commuteModeSelection: '',
    worksite: '',
    cyclingExperience: '',
    transitCombination: null,
    cargoOrKidsTransport: null,
    householdOwnsEbike: null,
    terrain: [],
    commuteFrequencyDaysPerWeek: null,
  })

/** True if all five KC questionnaire fields are ready for persistence. */
export function isKingCountyQuestionnaireComplete(
  v: KingCountyQuestionnaireValues
): boolean {
  return (
    v.commuteModeSelection !== '' &&
    v.commuteMode.trim() !== '' &&
    v.worksite.trim() !== '' &&
    v.cyclingExperience !== '' &&
    v.transitCombination !== null &&
    v.cargoOrKidsTransport !== null &&
    v.householdOwnsEbike !== null &&
    v.terrain.length > 0 &&
    v.commuteFrequencyDaysPerWeek !== null
  )
}

/** True if the address uses an email host allowed for the King County pilot. */
export function isKingCountyPilotEmail(email: string): boolean {
  const domain = extractDomainFromEmail(email.trim())
  return KC_ALLOWED_EMAIL_DOMAINS.some(
    (allowedDomain) => domain === allowedDomain
  )
}

/** King County e-bike subsidy employee FAQ. */
export const KC_EBIKE_SUBSIDY_FAQ_URL =
  'https://support.movewithfleet.com/en/articles/16805254-king-county-e-bike-subsidy-employee-faq'

/** Microsoft Form that replaced the in-app `/kc-employee-sign-up` flow. */
export const KC_EMPLOYEE_SIGNUP_FORM_URL =
  'https://forms.cloud.microsoft/pages/responsepage.aspx?id=mgXluvB210mZlnLf6V1pxwxL56xTbYROlMBn7_UP1llURU5KMDNXM0VHMEtYME5JTFpUUjk2WTBVMy4u&route=shorturl'

/** Prose before the terms-and-conditions link on the KC pilot signup form. */
export const KC_SIGNUP_ATTESTATION_BEFORE_TERMS_LINK =
  "By signing up, I confirm that I am a full-time, non-seasonal King County employee, at least 16 years old, and a Washington State resident. I commit to wearing a helmet when riding, engaging with safety education materials before my first ride, operating the bike only in class 1 or 2 mode, not modifying or tampering with the bike, logging my trips, and completing program surveys. I understand my personal information will only be used for administering the pilot program, that the program may be discontinued, and I accept Fleet's "
